import {findLast, isUndefined} from 'lodash';
import {connect} from 'react-redux';

import {AsyncMoreStatusesEnum} from '../../../../../libs/shared/core/src/helpers/types/asyncStatuses';
import {ViewerPanelId} from '../../../../../libs/shared/core/src/store/panels/panelStateBase';
import {
  AppParametricSelector,
  createSelector,
  makeMapDispatchToProps,
  makeMapStateToProps,
} from '../../../../../srcCommon/helpers/reselectHelpers';
import {isActivityInConversationRelevant} from '../../../../helpers/models/conversationHelpers';
import {ActivityModel} from '../../../../models/api/activityModel';
import {AppState} from '../../../../store/appState';
import {getUserId} from '../../../../store/data/teammates/teammatesSelectors';
import {jumpToActivityAsyncAction} from '../../../../store/panels/viewer/viewerPanelActions';
import {ViewerPanelConversationPageState} from '../../../../store/panels/viewer/viewerPanelState';
import {UnreadBannerAbove} from './unreadBannerAbove';

/*
 * Props.
 */

interface UnreadBannerAboveContainerProps {
  panelId: ViewerPanelId;
  activities: ReadonlyArray<ActivityModel>;
  firstVisibleActivityDate: number;
  page: ViewerPanelConversationPageState;
}

/*
 * Selectors.
 */

function getActivities(_state: AppState, props: UnreadBannerAboveContainerProps) {
  return props.activities;
}

function getFirstVisibleActivityDate(_state: AppState, props: UnreadBannerAboveContainerProps) {
  return props.firstVisibleActivityDate;
}

function getCurrentPage(_state: AppState, props: UnreadBannerAboveContainerProps) {
  return props.page;
}

function makeGetLastActivity() {
  return createSelector([getActivities], (activities) =>
    findLast(activities, isActivityInConversationRelevant),
  );
}

function makeGetFirstUnreadActivity() {
  return createSelector([getActivities, getCurrentPage], (activities, currentPage) => {
    const {originalConversationState} = currentPage;
    if (!originalConversationState) {
      return undefined;
    }

    const {seenAt} = originalConversationState;

    // If we haven't loaded activities beyond the seen date, we don't know what the first unread activity is.
    if (currentPage.topBoundary && (!seenAt || seenAt < currentPage.topBoundary.activityDate)) {
      return undefined;
    }

    return activities.find((a) => (!seenAt || seenAt < a.date) && isActivityInConversationRelevant(a));
  });
}

function makeGetHasUnreadActivityAbove(
  getFirstUnreadActivity: AppParametricSelector<UnreadBannerAboveContainerProps, ActivityModel | undefined>,
) {
  return createSelector(
    [getCurrentPage, getFirstVisibleActivityDate, getFirstUnreadActivity],
    (currentPage, firstVisibleActivityDate, firstUnreadActivity) => {
      if (currentPage.moreStatusTop === AsyncMoreStatusesEnum.LOADING) {
        return false;
      }

      if (!firstUnreadActivity) {
        return false;
      }

      if (!firstVisibleActivityDate) {
        return false;
      }

      return firstUnreadActivity.date < firstVisibleActivityDate;
    },
  );
}

const mapStateToProps = makeMapStateToProps<UnreadBannerAboveContainerProps>()(() => {
  const getLastActivity = makeGetLastActivity();
  const getFirstUnreadActivity = makeGetFirstUnreadActivity();
  const getHasUnreadActivityAbove = makeGetHasUnreadActivityAbove(getFirstUnreadActivity);
  return {
    hasUnreadActivityAbove: getHasUnreadActivityAbove,
    lastActivity: getLastActivity,
    firstUnreadActivity: getFirstUnreadActivity,
    userId: getUserId,
  };
});

const mapDispatchToProps = makeMapDispatchToProps<UnreadBannerAboveContainerProps>()(({panelId, page}) => ({
  onUnreadBannerClick: (activityId: number | undefined) =>
    jumpToActivityAsyncAction(
      panelId,
      isUndefined(activityId) ? {date: page.originalConversationState?.seenAt ?? 0} : {id: activityId},
    ),
}));

/*
 * Component.
 */

export const UnreadBannerAboveContainer = connect(mapStateToProps, mapDispatchToProps)(UnreadBannerAbove);
